import type { Evt } from '../envelope';
import type { Capabilities, DeviceProfile, HapticKind, PlatformBridge } from '../PlatformBridge';

/**
 * 순수 웹/PWA 어댑터. 네이티브 없이 웹 표준만으로 할 수 있는 만큼 한다.
 * 나머지는 조용히 no-op — 호출부가 플랫폼을 분기하지 않도록 하는 것이 이 계층의 목적이다.
 */
export class WebBridge implements PlatformBridge {
  readonly platform = 'web' as const;
  capabilities: Capabilities = {
    keepAwake: 'wakeLock' in navigator,
    immersive: typeof document.documentElement.requestFullscreen === 'function',
    haptics: typeof navigator.vibrate === 'function',
    fileExport: true,
    reminders: false,
    deviceProfile: true,
  };

  #wakeLock: WakeLockSentinel | null = null;
  #listeners = new Set<(e: Evt) => void>();
  #onVisibility = () => {
    this.#emit({ v: 1, kind: 'evt', topic: 'visibility', payload: { state: document.visibilityState } });
    // 화면이 돌아오면 wake lock 은 자동으로 풀려 있다. 다시 잡아준다.
    if (document.visibilityState === 'visible' && this.#wakeLock === null) void this.#reacquire();
  };
  #wantWakeLock = false;

  async init(): Promise<void> {
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  async keepAwake(on: boolean): Promise<void> {
    this.#wantWakeLock = on;
    if (!this.capabilities.keepAwake) return;
    if (on) {
      await this.#reacquire();
    } else {
      await this.#wakeLock?.release().catch(() => undefined);
      this.#wakeLock = null;
    }
  }

  async #reacquire(): Promise<void> {
    if (!this.#wantWakeLock || !this.capabilities.keepAwake) return;
    try {
      this.#wakeLock = await navigator.wakeLock.request('screen');
      this.#wakeLock.addEventListener('release', () => { this.#wakeLock = null; });
    } catch {
      // 배터리 절약 모드 등에서 거부될 수 있다. 치명적이지 않으므로 삼킨다.
      this.#wakeLock = null;
    }
  }

  async setImmersive(on: boolean): Promise<void> {
    try {
      if (on) {
        if (!document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
      // 사용자 제스처 없이 호출되면 거부된다. 호출부가 버튼 핸들러 안에서 부르도록 되어 있다.
    }
  }

  async haptic(kind: HapticKind): Promise<void> {
    if (!this.capabilities.haptics) return;
    const pattern = kind === 'error' ? [40, 60, 40] : kind === 'success' ? [18] : [8];
    navigator.vibrate(pattern);
  }

  async exportFile(name: string, mime: string, data: Blob): Promise<void> {
    const url = URL.createObjectURL(new Blob([data], { type: mime }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  async getDeviceProfile(): Promise<DeviceProfile> {
    return {
      platform: this.platform,
      userAgent: navigator.userAgent,
      model: null,
      osVersion: null,
      devicePixelRatio: window.devicePixelRatio,
      screenCssPx: { w: window.screen.width, h: window.screen.height },
      nativeRefreshHz: null,
    };
  }

  onNativeEvent(cb: (e: Evt) => void): () => void {
    this.#listeners.add(cb);
    return () => this.#listeners.delete(cb);
  }

  #emit(e: Evt): void {
    for (const l of this.#listeners) l(e);
  }

  dispose(): void {
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#listeners.clear();
    void this.#wakeLock?.release().catch(() => undefined);
  }
}
